import { inject, Injectable } from '@angular/core';
import { isFinanceVertical } from '@rma/generic/domain/helpers';
import { VerticalType } from '@rma/generic/domain/types';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { PublicProfileContextService, PublicUserContextService } from '@rma/public/accounts/user';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { ProductUserType, ProductUserTypeGrouping } from './domain/billing.model';
import { BillingUserContext } from './domain/billing-user-context.model';

@Injectable({
  providedIn: 'root',
})
export class BillingUserContextService {
  private readonly userService = inject(PublicUserContextService);
  private readonly profile = inject(PublicProfileContextService);

  public get userContext$(): Observable<BillingUserContext | null> {
    return this.userService.currentUser$.pipe(
      switchMap((user) => {
        // Consumers have no billing profile; everything else is resolved from the selected profile context.
        if (!user || user.userType === 'Consumer') {
          return of(null);
        }

        return combineLatest([
          this.profile.profileCode$,
          this.profile.primaryVerticalType$,
          this.profile.isIndividual$,
          this.profile.isTeam$,
        ]).pipe(map(([code, verticalType, isIndividual, isTeam]) => mapBillingUserContext(code, verticalType, isIndividual, isTeam)));
      }),
      rmaShareReplay(),
    );
  }
}

function mapBillingUserContext(code: string, verticalType: VerticalType, isIndividual: boolean, isTeam: boolean): BillingUserContext {
  const productUserType = isFinanceVertical(verticalType) ? ProductUserType.MortgageBroker : ProductUserType.RealEstate;

  if (isIndividual) {
    return { code, productUserType, productUserGroupingType: ProductUserTypeGrouping.Individual };
  }

  return { code, productUserType, productUserGroupingType: isTeam ? ProductUserTypeGrouping.Team : ProductUserTypeGrouping.Office };
}
